import { StartupScreen } from "@cliqsuite/ui";
import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { AuthProvider, useAuth } from "../lib/auth";
import { StoreScopeProvider, useStoreScope } from "../lib/storeScope";
import { RangeScopeProvider } from "../lib/rangeScope";
import { LoginScreen } from "../components/shell/LoginScreen";
import { VerifyEmailPage } from "../components/shell/VerifyEmailPage";
import { SetNewPasswordPage } from "../components/shell/SetNewPasswordPage";
import { AppShell } from "../components/shell/AppShell";
import { FirstStoreGate } from "../components/shell/FirstStoreGate";
import { SubscriptionGate } from "../components/shell/SubscriptionGate";
import { connectOwnerDb } from "../lib/db";

// verify-email/accept-invite/reset-password are only ever reached by
// clicking a link in an email — never through in-app navigation — so they
// live entirely outside this app's normal routeTree (whose child routes
// only ever render once inside AppShell's own <Outlet/>, i.e. only once
// already logged in). Checked by raw pathname before the auth gate below,
// so they work whether or not the browser already has a session.
function PublicLinkGate(): React.ReactNode {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const goHome = () => {
    window.location.href = "/";
  };
  if (pathname === "/verify-email" && token) return <VerifyEmailPage token={token} onContinue={goHome} />;
  if (pathname === "/accept-invite" && token) return <SetNewPasswordPage kind="invite" token={token} onDone={goHome} />;
  if (pathname === "/reset-password" && token) return <SetNewPasswordPage kind="reset" token={token} onDone={goHome} />;
  return null;
}

function Gate() {
  const { user, loading } = useAuth();
  const identity = user ? `${user.business_id}-${user.id}` : null;
  useEffect(() => {
    if (identity) void connectOwnerDb(identity);
  }, [identity]);
  const publicPage = PublicLinkGate();
  if (publicPage) return publicPage;
  if (loading && user) return <StartupScreen label="Checking your session" />;
  if (!user) return <LoginScreen />;
  return (
    <StoreScopeProvider>
      <ShellGate />
    </StoreScopeProvider>
  );
}

// Signup creates a first store transactionally. Keep this fallback for
// legacy or externally-created businesses that somehow have no store.
// The subscription gate sits outside it: a suspended business sees its notice, not "Add your first store".
function ShellGate() {
  const { user } = useAuth();
  const { stores, isLoading } = useStoreScope();
  if (!user) return null; // Gate above only renders this once user is set — narrows the type below.
  return (
    <SubscriptionGate businessId={user.business_id}>
      {!isLoading && stores.length === 0 ? (
        <FirstStoreGate businessId={user.business_id} />
      ) : (
        <RangeScopeProvider>
          <AppShell />
        </RangeScopeProvider>
      )}
    </SubscriptionGate>
  );
}

export function RootLayout() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}
